import { useRouter } from 'next/router';
import Script from 'next/script';

interface Props {
  children: React.ReactNode;
}

const isProd = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';

/**
 * Bridge Iubenda consent -> a single `iubenda:consent` CustomEvent.
 *
 * Consumers (e.g. the Facebook/Meta Pixel via `onIubendaConsent`) gate their
 * initialisation on this event so that tags NOT covered by Google Consent Mode
 * only fire after the user grants the relevant purpose. `detail.purposes`
 * follows the Iubenda standard (e.g. `purposes[5]` = Targeting & Advertising).
 *
 * We use the callbacks that receive the preference object explicitly.
 * `onPreferenceExpressedOrNotNeeded` covers both the page load with an
 * already-stored preference and the saving of preferences;
 * `onPreferenceExpressed` is a redundant backup for the save action.
 */
const IUBENDA_CONSENT_BRIDGE_SCRIPT = `
function __rwDispatchIubendaConsent(preference) {
  try {
    window.dispatchEvent(new CustomEvent("iubenda:consent", { detail: preference || { purposes: {} } }));
  } catch (e) {
    console.warn("[iubenda] failed to dispatch consent event", e);
  }
}
_iub.csConfiguration.callback = {
  onPreferenceExpressed: function(p) { __rwDispatchIubendaConsent(p); },
  onPreferenceExpressedOrNotNeeded: function(p) { __rwDispatchIubendaConsent(p); }
};
`;

export const IubendaProvider = ({ children }: Props) => {
  const router = useRouter();

  if (!isProd) return <>{children}</>;

  if (router?.asPath?.includes('getting-started')) return <>{children}</>;

  return (
    <>
      <Script
        id="iubenda-config"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          var _iub = _iub || [];
          _iub.csConfiguration = {
            "askConsentAtCookiePolicyUpdate": true,
            "cookiePolicyInOtherWindow": true,
            "perPurposeConsent": true,
            "reloadOnConsent": true,
            "siteId": 3424432,
            "cookiePolicyId": 25144422,
            "lang": "it",
            "i18n": {
              "it": {
                "banner": {
                  "dynamic": {
                    "body": "Usiamo cookie e tecnologie simili per analizzare il traffico, personalizzare contenuti e offrirti un'esperienza migliore. Puoi accettarli tutti, rifiutare quelli non essenziali o scegliere quali abilitare."
                  }
                }
              }
            },
            "cookiePolicyUrl": "https://www.restworld.it/cookie-policy",
            "banner": {
              "position": "float-bottom-center",
              "backgroundColor": "transparent",
              "backgroundOverlay": false,
              "textColor": "#1C1C33",
              "linksColor": "#6633BB",
              "fontSizeBody": "14px",
              "showTitle": false,
              "closeButtonDisplay": false,
              "listPurposes": true,
              "explicitWithdrawal": true,
              "acceptButtonDisplay": true,
              "acceptButtonColor": "#6633BB",
              "acceptButtonCaptionColor": "#FFFFFF",
              "acceptButtonCaption": "Accetta tutti",
              "rejectButtonDisplay": true,
              "rejectButtonColor": "#FAFAFA",
              "rejectButtonCaptionColor": "#1C1C33",
              "rejectButtonCaption": "Rifiuta non essenziali",
              "customizeButtonDisplay": true,
              "customizeButtonColor": "#FAFAFA",
              "customizeButtonCaptionColor": "#6633BB",
              "customizeButtonCaption": "Personalizza"
            }
          };
          ${IUBENDA_CONSENT_BRIDGE_SCRIPT}
        `,
        }}
      />
      <Script src="https://cs.iubenda.com/autoblocking/3424432.js" strategy="afterInteractive" />
      <Script src="//cdn.iubenda.com/cs/iubenda_cs.js" strategy="afterInteractive" />
      {children}
    </>
  );
};
